import React, { useEffect, useMemo, useState } from 'react';
import { reportHandledError } from '../utils/sentry';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { buildClientAccountTimeline } from '../utils/clientBalances';
import {
  buildSalePaymentTerms,
  formatPaymentConditionLabel,
  getClientPaymentConditionDays,
  summarizeReceivables,
} from '../utils/salesReceivables';
import {
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  setSalePaymentTermsColumnsSupport,
} from '../utils/salesPaymentTermsSupport';
import { attachStoreOrdersToSales } from '../utils/saleStoreOrders';
import { getSaleItemDisplayName } from '../utils/productLabels';
import { 
  ArrowLeft, Edit3, Mail, Phone, MapPin, 
  User, Briefcase, Clock, Plus, X, FileText,
  RefreshCcw, Smartphone, 
  MapPinned, DollarSign
} from 'lucide-react';

/**
 * Legajo Técnico de Cliente - Diseño Enterprise Alta Densidad
 * Optimizada para gestión de cuenta corriente y perfiles impositivos.
 */
const CLIENT_DETAIL_SALES_BASE_SELECT =
  'id, client_id, created_at, total, status, active, payment_method, invoice_status, numero_factura, billing_source, sitios(nombre)';
const CLIENT_DETAIL_SALES_SELECT = `${CLIENT_DETAIL_SALES_BASE_SELECT}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;
const normalizeForgedSaleMovementTag = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const normalizeMovementTypeTag = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const isForgedSaleTimelineEntry = (entry) => {
  if (entry?.entryKind === 'sale') return true;

  const typeTag = normalizeForgedSaleMovementTag(entry?.type);
  if (typeTag.includes('ventapos') || typeTag.includes('ventaonline')) {
    return true;
  }

  if (!entry?.sale_id) return false;

  const observationTag = normalizeForgedSaleMovementTag(entry?.observations);
  return (
    observationTag.includes('ventapos') ||
    observationTag.includes('ventaonline')
  );
};

async function fetchClientSalesRows({ clientId, organizationId }) {
  const buildQuery = (selectClause) => {
    let query = supabase
      .from('sales')
      .select(selectClause)
      .eq('client_id', clientId);

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    return query.order('created_at', { ascending: false });
  };

  const support = getSalePaymentTermsColumnsSupport();

  if (support !== false) {
    const response = await buildQuery(CLIENT_DETAIL_SALES_SELECT);

    if (!response.error) {
      setSalePaymentTermsColumnsSupport(true);
      return attachStoreOrdersToSales({
        sales: (response.data || []).map(normalizeSalePaymentTermsRow),
        organizationId,
      });
    }

    if (!isMissingSalePaymentTermsColumnsError(response.error)) {
      return response;
    }

    setSalePaymentTermsColumnsSupport(false);
  }

  const fallbackResponse = await buildQuery(CLIENT_DETAIL_SALES_BASE_SELECT);

  if (fallbackResponse.error) {
    return fallbackResponse;
  }

  return attachStoreOrdersToSales({
    sales: (fallbackResponse.data || []).map(normalizeSalePaymentTermsRow),
    organizationId,
  });
}

function ClientesDetalle({ onNavigate }) {
  const { id } = useParams(); // Sincronización con Rutas
  const navigate = useNavigate();
  const { profile, organization } = useAuth();
  const allowClientDeactivation = organization?.allow_client_deactivation === true;
  const [cliente, setCliente] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [ventasCliente, setVentasCliente] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uiMessage, setUiMessage] = useState(null); // { type, text }

  // Detalle de movimiento (inline; evita modales/popouts)
  const [selectedMov, setSelectedMov] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSale, setDetailSale] = useState(null);
  const [detailSaleItems, setDetailSaleItems] = useState([]);

  // Estilos de tipo de movimiento (evitamos “rojo” para Ventas; dejamos semántica: verde=cobranza, azul=operación)
  const shouldUseForgedSaleTimezone = (entry) =>
    isForgedSaleTimelineEntry(entry);

  const formatDateTime = (value, { utc = false } = {}) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const timeZone = utc ? 'UTC' : undefined;
    return `${date.toLocaleDateString('es-AR', { timeZone })} ${date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    })}`;
  };

  const formatDateLabel = (value, { utc = false } = {}) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: utc ? 'UTC' : undefined,
    });
  };

  const formatTimeLabel = (value, { utc = false } = {}) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: utc ? 'UTC' : undefined,
    });
  };

  const formatTimelineDateTime = (entry) =>
    formatDateTime(entry?.created_at, { utc: shouldUseForgedSaleTimezone(entry) });

  const formatTimelineDateLabel = (entry) =>
    formatDateLabel(entry?.created_at, { utc: shouldUseForgedSaleTimezone(entry) });

  const formatTimelineTimeLabel = (entry) =>
    formatTimeLabel(entry?.created_at, { utc: shouldUseForgedSaleTimezone(entry) });

  const getMovementMeta = (movement) => {
    const rawAmount = Number(
      movement?.amount ?? (movement?.entryKind === 'sale' ? movement?.total : 0) ?? 0,
    );
    const lowersDebt = rawAmount < 0;
    const amountAbs = Math.abs(rawAmount);
    const signedAmount = `${lowersDebt ? '-' : '+'} $ ${fmtMoney(amountAbs)}`;
    const typeTag = normalizeMovementTypeTag(movement?.type);

    // Venta directa (entrada de venta sin movimiento de cuenta asociado).
    if (movement?.entryKind === 'sale') {
      return {
        amountAbs,
        lowersDebt: false,
        impactLabel: 'Venta',
        impactHint: 'Venta a cuenta corriente.',
        signedAmount: `+ $ ${fmtMoney(amountAbs)}`,
        badgeClass: 'bg-blue-50 text-blue-700 border border-blue-100',
        accentClass: 'text-blue-600',
        softCardClass: 'border-blue-200 bg-blue-50',
      };
    }

    // Liquidaciones de CRM (hito / proyecto / ticket).
    if (typeTag.includes('liquidacion')) {
      return {
        amountAbs,
        lowersDebt,
        impactLabel: 'Liquidación CRM',
        impactHint: lowersDebt
          ? 'Anulación de liquidación CRM.'
          : 'Liquidación de CRM enviada a cuenta corriente.',
        signedAmount,
        badgeClass: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
        accentClass: lowersDebt ? 'text-emerald-600' : 'text-indigo-600',
        softCardClass: lowersDebt
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-indigo-200 bg-indigo-50',
      };
    }

    // Gastos de proyecto cargados al cliente.
    if (typeTag.includes('gasto')) {
      return {
        amountAbs,
        lowersDebt,
        impactLabel: 'Gasto de proyecto',
        impactHint: lowersDebt
          ? 'Reversión de gasto de proyecto.'
          : 'Gasto de proyecto cargado al cliente.',
        signedAmount,
        badgeClass: 'bg-orange-50 text-orange-700 border border-orange-100',
        accentClass: lowersDebt ? 'text-emerald-600' : 'text-orange-600',
        softCardClass: lowersDebt
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-orange-200 bg-orange-50',
      };
    }

    // Cobranzas / ajustes a favor (bajan la deuda).
    if (lowersDebt) {
      return {
        amountAbs,
        lowersDebt,
        impactLabel: 'Cobranza',
        impactHint: 'Cobranza o ajuste a favor.',
        signedAmount,
        badgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
        accentClass: 'text-emerald-600',
        softCardClass: 'border-emerald-200 bg-emerald-50',
      };
    }

    // Otros cargos que suman saldo.
    return {
      amountAbs,
      lowersDebt,
      impactLabel: 'Aumenta deuda',
      impactHint: 'Cargo que suma saldo.',
      signedAmount,
      badgeClass: 'bg-amber-50 text-amber-700 border border-amber-100',
      accentClass: 'text-amber-600',
      softCardClass: 'border-amber-200 bg-amber-50',
    };
  };

  const getMovTypeClass = (movement, withBorder = false) => {
    const meta = getMovementMeta(movement);
    if (withBorder) return meta.badgeClass;
    return meta.lowersDebt ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
  };

  useEffect(() => {
    if (!id || !profile?.organization_id) return;
    fetchData();
    // eslint-disable-next-line
  }, [id, profile?.organization_id]);

  async function fetchData() {
    if (!id || !profile?.organization_id) {
      return;
    }

    setLoading(true);
    try {
      let cliQuery = supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .eq('organization_id', profile.organization_id);
      const movsQuery = supabase
        .from('client_movements')
        .select('*')
        .eq('client_id', id)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });
      const receivablesQuery = supabase
        .from('sales_receivables')
        .select('id, sale_id, due_date, pending_amount, settled_amount, total_amount, description, created_at')
        .eq('client_id', id)
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .gt('pending_amount', 0)
        .order('due_date', { ascending: true });

      const [cliRes, movsRes, salesRes, receivablesRes] = await Promise.all([
        cliQuery.single(),
        movsQuery,
        fetchClientSalesRows({
          clientId: id,
          organizationId: profile.organization_id,
        }),
        receivablesQuery,
      ]);

      const cli = cliRes.data;
      const movs = movsRes.data;
      const sales = salesRes.data;
      const receivablesData = receivablesRes.data;
      if (cliRes.error) throw cliRes.error;
      if (movsRes.error) throw movsRes.error;
      if (salesRes.error) throw salesRes.error;
      if (receivablesRes.error) throw receivablesRes.error;

      const normalizedClient = cli
        ? {
            ...cli,
            default_payment_condition: formatPaymentConditionLabel(getClientPaymentConditionDays(cli)),
          }
        : cli;

      setCliente(normalizedClient);
      setMovimientos(movs || []);
      setVentasCliente(sales || []);
      setReceivables(receivablesData || []);
    } catch (error) {
      console.error(error);
      reportHandledError(error, { feature: 'clientes:legajo' });
      setUiMessage({ type: 'error', text: 'Error al sincronizar el legajo. Verificá conexión/permisos.' });
    } finally {
      setLoading(false);
    }
  }

  const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

  const movementTimeline = useMemo(
    () => buildClientAccountTimeline({
      client: cliente,
      movements: movimientos,
      sales: ventasCliente,
    }),
    [cliente, movimientos, ventasCliente],
  );
  const computedCurrentBalance = movementTimeline.length > 0
    ? Number(movementTimeline[0]?.current_balance || 0)
    : Number(cliente?.initial_balance ?? cliente?.current_balance ?? cliente?.balance ?? 0);
  const receivableSummary = useMemo(
    () => summarizeReceivables(receivables),
    [receivables],
  );

  useEffect(() => {
    if (!selectedMov) return;

    const stillExists = movementTimeline.some((entry) => entry.timelineKey === selectedMov.timelineKey);
    if (stillExists) return;

    setSelectedMov(null);
    setDetailSale(null);
    setDetailSaleItems([]);
  }, [movementTimeline, selectedMov]);

  const openMovimientoDetalle = async (mov) => {
    // Toggle
    if (selectedMov?.timelineKey === mov?.timelineKey) {
      setSelectedMov(null);
      setDetailSale(null);
      setDetailSaleItems([]);
      return;
    }

    setSelectedMov(mov);
    setDetailSale(null);
    setDetailSaleItems([]);

    // Si el movimiento referencia una venta, cargamos el detalle (venta + ítems)
    const saleId = mov?.sale_id;
    if (!saleId) return;

    setDetailLoading(true);
    try {
      // Venta
      const detailSaleBaseSelect =
        'id, created_at, total, invoice_status, numero_factura, cae, cae_vencimiento, site_id, sitios(nombre)';
      const detailSaleSelect = `${detailSaleBaseSelect}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;
      let sQ = supabase.from('sales').select(detailSaleSelect).eq('id', saleId);
      if (profile?.organization_id) sQ = sQ.eq('organization_id', profile.organization_id);
      let { data: sale, error: saleErr } = await sQ.single();

      if (saleErr && isMissingSalePaymentTermsColumnsError(saleErr)) {
        setSalePaymentTermsColumnsSupport(false);
        let fallbackQuery = supabase
          .from('sales')
          .select(detailSaleBaseSelect)
          .eq('id', saleId);
        if (profile?.organization_id) fallbackQuery = fallbackQuery.eq('organization_id', profile.organization_id);
        const fallbackResponse = await fallbackQuery.single();
        sale = fallbackResponse.data;
        saleErr = fallbackResponse.error;
      } else if (!saleErr) {
        setSalePaymentTermsColumnsSupport(true);
      }

      if (saleErr) throw saleErr;
      setDetailSale(normalizeSalePaymentTermsRow(sale || null));

      // Ítems
      const { data: items, error: itErr } = await supabase
        .from('sale_items')
        .select('id, quantity, unit_price, product_id, product_name_snapshot')
        .eq('sale_id', saleId)
        .order('created_at', { ascending: true });
      if (itErr) throw itErr;

      const productIds = Array.from(
        new Set((items || []).map((item) => item.product_id).filter(Boolean)),
      );
      let productsById = new Map();

      if (productIds.length > 0) {
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('id, name, barcode')
          .in('id', productIds);
        if (productsError) throw productsError;

        productsById = new Map((productsData || []).map((product) => [product.id, product]));
      }

      setDetailSaleItems(
        (items || []).map((item) => ({
          ...item,
          products: item.product_id ? productsById.get(item.product_id) || null : null,
        })),
      );
    } catch (e) {
      console.error(e);
      setUiMessage({ type: 'error', text: 'No se pudo cargar el detalle del movimiento.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRegisterMovement = () => {
    navigate('/sistema/tesoreria-cobranzas', { state: { clientId: id } });
  };

  const handleEditCliente = () => {
    // Evitamos depender del router interno (onNavigate) para no caer en /clientes-nuevo.
    // Ruta definida en Sistema.jsx: /sistema/clientes-editar/:id
    navigate(`/sistema/clientes-editar/${id}`);
  };

  const handleAjusteCuentaCorriente = () => {
    navigate(`/sistema/clientes-movimiento/${id}`);
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-3 text-[#51A7FD]" size={30} />
      <p className="font-black uppercase tracking-[0.2em] text-slate-400 text-[9px]">Sincronizando Legajo...</p>
    </div>
  );

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER: TÍTULO + ACCIONES */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('clientes')} className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all">
            <ArrowLeft size={20} />
          </button>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <User size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 uppercase tracking-tight leading-none">
              {cliente?.full_name || 'Detalle de Cliente'}
            </h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Legajo comercial, fiscal y cuenta corriente
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <button
            onClick={handleRegisterMovement}
            className="flex-1 sm:flex-none justify-center bg-[#3ff2aa] text-[#0B2A36] px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-md hover:scale-[1.02] active:scale-95 transition-all"
          >
            <DollarSign size={16} /> Registrar Cobranza
          </button>
          <button
            onClick={handleAjusteCuentaCorriente}
            className="flex-1 sm:flex-none justify-center bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-sm hover:scale-[1.02] active:scale-95 transition-all"
          >
            <FileText size={16} /> Ajuste Cta Cte
          </button>
          <button 
            onClick={handleEditCliente}
            className="flex-1 sm:flex-none justify-center bg-[#51A7FD] text-white px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-md hover:scale-[1.02] active:scale-95 transition-all"
          >
            <Edit3 size={16} /> Editar Perfil
          </button>
        </div>
      </header>

      {/* Mensajería inline (sin popouts) */}
      {uiMessage?.text && (
        <div
          className={`mb-3 rounded-2xl border px-4 py-3 text-[11px] font-bold ${
            uiMessage.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-700'
              : uiMessage.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-blue-50 border-blue-200 text-blue-700'
          }`}
        >
          {uiMessage.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-white/70">Saldo actual</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            $ {fmtMoney(computedCurrentBalance)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cuenta corriente vigente</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-white/70">Deuda exigible</p>
            <Clock size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-amber-600 tracking-tight">
            $ {fmtMoney(receivableSummary.totalExigible)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Vencida o exigible hoy
          </p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-white/70">Credito</p>
            <Briefcase size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-emerald-600 tracking-tight">
            $ {fmtMoney(cliente?.credit_limit ?? cliente?.credit_limit_amount ?? 0)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Limite asignado</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-white/70">Movimientos</p>
            <FileText size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight">{movementTimeline.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ventas y cobros</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {/* COLUMNA IZQUIERDA: IDENTIDAD */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <User size={16} className="text-[#3FF2AA]" />
              <span className="text-[9px] font-black text-white uppercase tracking-widest">Información de Contacto y Ubicación</span>
            </div>
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              {[
                { label: 'Nombre Completo', val: cliente.full_name || 'S/D', icon: <User size={14}/> },
                { label: 'Email Principal', val: cliente.email || 'S/D', icon: <Mail size={14}/> },
                { label: 'Teléfono', val: cliente.phone || cliente.mobile || 'S/D', icon: <Phone size={14}/> },
                { label: 'WhatsApp', val: cliente.whatsapp || 'S/D', icon: <Smartphone size={14}/> },
                { label: 'Dirección', val: cliente.address || 'S/D', icon: <MapPin size={14}/> },
                { label: 'Ciudad', val: cliente.city || 'S/D', icon: <MapPinned size={14}/> },
                { label: 'Provincia', val: cliente.state || cliente.province || 'S/D', icon: <MapPinned size={14}/> },
                { label: 'CP', val: cliente.zip_code || cliente.postal_code || 'S/D', icon: <MapPinned size={14}/> }
              ].map((item, i) => (
                <div key={i}>
                  <label className="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em] block mb-1.5">{item.label}</label>
                  <div className="flex items-center gap-2 text-[12px] font-black text-slate-800 uppercase tracking-tight">
                    <span className="text-slate-300">{item.icon}</span> {item.val}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: FISCAL / COMERCIAL (sin ocultar campos) */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <Briefcase size={16} className="text-[#3FF2AA]" />
              <span className="text-[9px] font-black text-white uppercase tracking-widest">Configuración Impositiva</span>
            </div>
            <div className="p-3 space-y-2">
              {[
                { label: 'Condición ante IVA', val: cliente.tax_condition || cliente.iva_condition || 'S/D', bold: true },
                { label: 'Tipo de Documento', val: cliente.doc_type || 'S/D' },
                { label: 'Nro de Documento', val: cliente.doc_number || 'S/D', bold: true },
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{row.label}</span>
                  <span className={`text-[10px] font-black uppercase tracking-tight ${row.bold ? 'text-slate-900' : 'text-slate-500'}`}>{row.val}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center gap-2">
              <DollarSign size={16} className="text-[#3FF2AA]" />
              <span className="text-[9px] font-black text-white uppercase tracking-widest">Comercial y Cuenta Corriente</span>
            </div>
            <div className="p-3 space-y-2">
              {[
                { label: 'Condición de Pago Default', val: cliente.default_payment_condition || 'S/D' },
                { label: 'Límite de Crédito', val: `$ ${fmtMoney(cliente.credit_limit ?? cliente.credit_limit_amount ?? 0)}` },
                { label: 'Saldo Inicial', val: `$ ${fmtMoney(cliente.initial_balance ?? 0)}` },
                { label: 'Saldo Actual', val: `$ ${fmtMoney(computedCurrentBalance)}`, bold: true },
                ...(allowClientDeactivation
                  ? [{ label: 'Estado', val: (cliente.is_active === false ? 'INACTIVO' : 'ACTIVO'), bold: true }]
                  : [])
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{row.label}</span>
                  <span className={`text-[10px] font-black uppercase tracking-tight ${row.bold ? 'text-slate-900' : 'text-slate-500'}`}>{row.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TABLA DE MOVIMIENTOS (Audit Style) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center text-[#3FF2AA]"><FileText size={18}/></div>
            <div>
              <h3 className="text-sm font-black text-white uppercase tracking-tight">Movimientos de Cuenta Corriente</h3>
              <p className="text-[8px] font-black text-white/60 uppercase tracking-widest mt-1">Ventas, cobros, liquidaciones CRM y gastos de proyecto</p>
            </div>
          </div>
        </div>
        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
          {movementTimeline.map((m) => {
            const meta = getMovementMeta(m);

            return (
              <button
                key={`mobile-${m.timelineKey}`}
                onClick={() => openMovimientoDetalle(m)}
                className={`w-full text-left rounded-2xl border bg-white p-3 shadow-sm transition-all ${
                  selectedMov?.timelineKey === m.timelineKey ? 'border-[#51A7FD] ring-2 ring-[#51A7FD]/10' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{formatTimelineDateTime(m)}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${getMovTypeClass(m, true)}`}>
                        {m.type || 'Movimiento'}
                      </span>
                    </div>
                  </div>
                  <div className={`shrink-0 rounded-2xl border px-3 py-2 text-right ${meta.softCardClass}`}>
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-500">Importe</p>
                    <p className={`mt-1 text-[12px] font-black tracking-tight ${meta.accentClass}`}>{meta.signedAmount}</p>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Detalle</p>
                    <p className="mt-1 text-[10px] font-bold text-slate-600">{m.observations || meta.impactHint}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Saldo</p>
                    <p className="mt-1 text-[11px] font-black text-slate-800">$ {fmtMoney(m.current_balance)}</p>
                  </div>
                </div>
              </button>
            );
          })}
          {movementTimeline.length === 0 && (
            <div className="py-16 text-center opacity-30 italic flex flex-col items-center">
              <FileText size={40} className="mb-3 text-slate-200" />
              <p className="text-[10px] font-black uppercase tracking-[0.2em]">Sin movimientos ni ventas registradas</p>
            </div>
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[1080px] text-left">
            <thead>
              <tr className="bg-[#0B2A36] border-b border-white/10 text-[9px] font-black text-white/70 uppercase tracking-widest">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Movimiento</th>
                <th className="px-4 py-3">Detalle</th>
                <th className="px-4 py-3 text-right">Importe</th>
                <th className="px-4 py-3 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
              {movementTimeline.map((m) => {
                const meta = getMovementMeta(m);

                return (
                  <tr
                    key={m.timelineKey}
                    onClick={() => openMovimientoDetalle(m)}
                    className={`cursor-pointer align-top transition-all hover:bg-blue-50/20 ${
                      selectedMov?.timelineKey === m.timelineKey ? 'bg-blue-50/30' : ''
                    }`}
                    title="Click para ver detalle"
                  >
                    <td className="px-4 py-3">
                      <div className="text-[10px] font-black uppercase tracking-tight text-slate-700">
                        {formatTimelineDateLabel(m)}
                      </div>
                      <div className="mt-1 text-[10px] font-bold text-slate-400">
                        {formatTimelineTimeLabel(m)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <span className={`rounded-lg px-3 py-1 text-[9px] font-black uppercase tracking-widest ${getMovTypeClass(m, true)}`}>
                          {m.type || 'Movimiento'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[10px] font-bold leading-relaxed text-slate-600">
                        {m.observations || meta.impactHint}
                      </p>
                      <p className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                        {m.entryKind === 'sale'
                          ? `Venta #${String(m.sale_id).slice(0, 8)}`
                          : m.sale_id
                            ? `Venta asociada #${String(m.sale_id).slice(0, 8)}`
                            : 'Movimiento manual'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className={`text-[13px] font-black tracking-tight ${meta.accentClass}`}>
                        {meta.signedAmount}
                      </div>
                      <p className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                        Importe del movimiento
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-[13px] font-black tracking-tight text-slate-800">
                        $ {fmtMoney(m.current_balance)}
                      </div>
                      <p className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                        Saldo acumulado
                      </p>
                    </td>
                  </tr>
                );
              })}
              {movementTimeline.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-20 text-center">
                    <div className="flex flex-col items-center italic opacity-30">
                      <FileText size={40} className="mb-3 text-slate-200" />
                      <p className="text-[10px] font-black uppercase tracking-[0.2em]">Sin movimientos ni ventas registradas</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* DETALLE INLINE DEL MOVIMIENTO */}
        {selectedMov && (
          <div className="border-t border-slate-100 bg-slate-50/40 p-3 md:p-4">
            {(() => {
              const meta = getMovementMeta(selectedMov);
              const saleTerms = selectedMov.sale_id
                ? buildSalePaymentTerms({
                    sale: detailSale || {
                      created_at: selectedMov.created_at,
                      payment_condition_label: selectedMov.payment_condition_label,
                      payment_due_date: selectedMov.payment_due_date,
                    },
                    client: cliente,
                  })
                : null;

              return (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Detalle del movimiento</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-lg px-3 py-1 text-[9px] font-black uppercase tracking-widest ${getMovTypeClass(selectedMov, true)}`}>
                          {selectedMov.type || 'Movimiento'}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-tight text-slate-600">
                          {formatTimelineDateTime(selectedMov)}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => openMovimientoDetalle(selectedMov)}
                      className="rounded-xl border border-[#51A7FD] bg-[#51A7FD] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white transition-all hover:opacity-90"
                      title="Cerrar detalle"
                    >
                      Cerrar
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-12">
                    <div className={`rounded-2xl border bg-white p-4 shadow-sm xl:col-span-3 ${meta.softCardClass}`}>
                      <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Importe</p>
                      <p className={`mt-2 text-2xl font-black tracking-tight ${meta.accentClass}`}>
                        {meta.signedAmount}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        {meta.impactHint}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-3">
                      <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Saldo</p>
                      <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                        $ {fmtMoney(selectedMov.current_balance)}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Balance acumulado del cliente
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-3">
                      <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Referencia</p>
                      <p className="mt-2 text-[14px] font-black tracking-tight text-slate-800">
                        {selectedMov.entryKind === 'sale' ? 'Venta registrada' : selectedMov.sale_id ? 'Venta asociada' : 'Movimiento manual'}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {selectedMov.sale_id ? `ID ${selectedMov.sale_id}` : 'Sin comprobante de venta'}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-3">
                      <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Resumen</p>
                      <p className="mt-2 text-[11px] font-bold leading-relaxed text-slate-600">
                        {selectedMov.observations || meta.impactHint}
                      </p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {selectedMov.type || 'Movimiento'}
                      </p>
                    </div>

                    {selectedMov.sale_id && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-3">
                        <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Condicion de pago</p>
                        <p className="mt-2 text-[14px] font-black tracking-tight text-slate-800 uppercase">
                          {saleTerms?.label || selectedMov.payment_condition_label || 'N/A'}
                        </p>
                        <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Definida al momento de la venta
                        </p>
                      </div>
                    )}

                    {selectedMov.sale_id && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:col-span-3">
                        <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Vencimiento</p>
                        <p className="mt-2 text-[14px] font-black tracking-tight text-slate-800">
                          {saleTerms?.dueDate
                            ? formatDateLabel(saleTerms.dueDate, { utc: true })
                            : '-'}
                        </p>
                        <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Fecha pactada del comprobante
                        </p>
                      </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm xl:col-span-12">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-400">Información ampliada</p>
                        {detailLoading && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <RefreshCcw size={14} className="animate-spin" />
                            <span className="text-[9px] font-black uppercase tracking-widest">Cargando...</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">Observaciones</p>
                          <p className="mt-1 text-[11px] font-bold leading-relaxed text-slate-600">
                            {selectedMov.observations || 'Sin observaciones.'}
                          </p>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-4 py-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">Lectura rápida</p>
                          <p className="mt-1 text-[11px] font-bold leading-relaxed text-slate-600">
                            {selectedMov.entryKind === 'sale'
                              ? 'Esta venta se suma al saldo del cliente.'
                              : meta.lowersDebt
                                ? 'Este movimiento reduce el saldo del cliente.'
                                : 'Este movimiento suma saldo al cliente.'}
                          </p>
                        </div>
                      </div>

                      {selectedMov.sale_id && (
                        <div className="mt-5 border-t border-slate-100 pt-5">
                          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Detalle de venta</p>
                          {!detailSale && !detailLoading && (
                            <p className="mt-2 text-[11px] font-bold text-slate-400">No se pudo obtener el comprobante asociado.</p>
                          )}

                          {detailSale && (
                            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-12">
                              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 md:col-span-4">
                                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</p>
                                <p className="mt-1 text-[14px] font-black text-slate-900">$ {fmtMoney(detailSale.total)}</p>
                                <p className="mt-2 text-[8px] font-black uppercase tracking-widest text-slate-400">Sucursal</p>
                                <p className="mt-1 text-[10px] font-bold text-slate-600">{detailSale.sitios?.nombre || '-'}</p>
                              </div>
                              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 md:col-span-8">
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Estado AFIP</p>
                                    <p className="mt-1 text-[10px] font-black uppercase text-slate-700">{detailSale.invoice_status || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">N° Comp.</p>
                                    <p className="mt-1 text-[10px] font-bold text-slate-600">{detailSale.numero_factura || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">CAE</p>
                                    <p className="mt-1 text-[10px] font-bold text-slate-600">{detailSale.cae || '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Vto. CAE</p>
                                    <p className="mt-1 text-[10px] font-bold text-slate-600">{detailSale.cae_vencimiento ? formatDateLabel(detailSale.cae_vencimiento, { utc: true }) : '-'}</p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Condicion</p>
                                    <p className="mt-1 text-[10px] font-black uppercase text-slate-700">
                                      {saleTerms?.label || detailSale.payment_condition_label || '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Vencimiento</p>
                                    <p className="mt-1 text-[10px] font-bold text-slate-600">
                                      {saleTerms?.dueDate ? formatDateLabel(saleTerms.dueDate, { utc: true }) : '-'}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="md:col-span-12">
                                <p className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-300">Items</p>
                                <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-100">
                                  <table className="w-full bg-white text-left">
                                    <thead className="bg-[#0B2A36]">
                                      <tr className="text-[9px] font-black uppercase tracking-widest text-white/70">
                                        <th className="px-4 py-3">Producto</th>
                                        <th className="px-4 py-3 text-center">Cant.</th>
                                        <th className="px-4 py-3 text-right">Unit.</th>
                                        <th className="px-4 py-3 text-right">Subtotal</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                      {(detailSaleItems || []).map((it) => {
                                        const qty = Math.abs(Number(it.quantity || 0));
                                        const unit = Math.abs(Number(it.unit_price || 0));
                                        const sub = qty * unit;
                                        const name = getSaleItemDisplayName(it);
                                        return (
                                          <tr key={it.id} className="text-[10px] font-bold text-slate-600">
                                            <td className="px-4 py-3">
                                              <div className="font-black uppercase text-slate-800">{name}</div>
                                              <div className="text-[9px] font-bold text-slate-400">{it.products?.barcode || ''}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center">{qty}</td>
                                            <td className="px-4 py-3 text-right">$ {fmtMoney(unit)}</td>
                                            <td className="px-4 py-3 text-right">$ {fmtMoney(sub)}</td>
                                          </tr>
                                        );
                                      })}
                                      {(detailSaleItems || []).length === 0 && !detailLoading && (
                                        <tr>
                                          <td colSpan="4" className="px-4 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                                            Sin items
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default ClientesDetalle;

